import {
  Checklist,
  Edit,
  Lock,
  Settings,
} from "@mui/icons-material"
import {
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { RootState } from "../../../app/store"
import { useNavigate } from "react-router-dom"
import LoadingIndicator from "../../../app/components/loading-indicator"
import {
  setDialogRaffleSureNumberWinnersAct,
  setRaffleForDisableAct,
  showRaffleSettingsDialogAct,
} from "../rafles-list.slice"
import { RaffleUtils } from "../../../utils/raffle.utils"
import RaffleSettingsDialog from "./raffle-settings-dialog"

const denseCell = { py: 0.5, px: 1.25, fontSize: "0.8125rem" }
const denseHead = { ...denseCell, fontWeight: 600, whiteSpace: "nowrap" as const }

const STATUS_CONFIG: Record<
  number,
  { label: string; color: "success" | "warning" | "default" }
> = {
  0: { label: "Activo", color: "success" },
  1: { label: "Bloqueado", color: "warning" },
  2: { label: "Finalizado", color: "default" },
}

function getStatusConfig(status: number) {
  return STATUS_CONFIG[status] ?? { label: "--", color: "default" as const }
}

function formatDate(dateStr: string) {
  return dateStr.split("T")[0]
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(value)
}

export default function RafflesList() {
  const { raffles, loading } = useAppSelector((state: RootState) => state.raflesList)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const editRaffle = (raffleId: string) => {
    navigate(`/dashboard/handel-raffle/${raffleId}`)
  }

  return (
    <>
      <RaffleSettingsDialog />
      <LoadingIndicator open={loading} />

      <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: "hidden" }}>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableCell sx={denseHead}>Sorteo</TableCell>
                <TableCell sx={denseHead}>Fecha</TableCell>
                <TableCell sx={denseHead}>Estado</TableCell>
                <TableCell sx={denseHead} align="right">
                  Precio
                </TableCell>
                <TableCell sx={denseHead} align="center">
                  Imgs
                </TableCell>
                <TableCell sx={denseHead} align="right">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {raffles.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No hay sorteos registrados. Crea el primero con &quot;Nuevo sorteo&quot;.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {raffles.map((raffle) => {
                const status = getStatusConfig(raffle.status)
                const sureWinnersCount = raffle.numberSureWinners?.length ?? 0

                return (
                  <TableRow
                    key={raffle._id}
                    hover
                    sx={{
                      transition: "background-color 0.15s",
                      "&:last-child td": { border: 0 },
                    }}
                  >
                    <TableCell sx={denseCell}>
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
                        {raffle.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {raffle.code}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...denseCell, whiteSpace: "nowrap" }}>
                      {formatDate(raffle.datePrize)}
                    </TableCell>
                    <TableCell sx={denseCell}>
                      <Chip
                        size="small"
                        label={status.label}
                        color={status.color}
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem" }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...denseCell, whiteSpace: "nowrap" }} align="right">
                      {formatCurrency(raffle.ticketPrice)}
                    </TableCell>
                    <TableCell sx={denseCell} align="center">
                      <Typography variant="caption" color="text.secondary">
                        {raffle.images?.length ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell sx={denseCell} align="right">
                      <Stack direction="row" spacing={0} justifyContent="flex-end" alignItems="center">
                        <Tooltip title={`Ganadores seguros (${sureWinnersCount})`}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              dispatch(
                                setDialogRaffleSureNumberWinnersAct({
                                  raffleId: raffle._id,
                                  raffleName: raffle.name,
                                  inputNumber: "",
                                  nTicket: 0,
                                  maxLength: raffle.numberLength,
                                  numbers: raffle.numberSureWinners,
                                  prize: 0,
                                }),
                              )
                            }
                            sx={{ cursor: "pointer", p: 0.5 }}
                          >
                            <Checklist sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Configuración">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() =>
                              dispatch(
                                showRaffleSettingsDialogAct({
                                  raffleId: raffle._id,
                                  raffleName: raffle.name,
                                  showPercentage: raffle.showPercentage,
                                }),
                              )
                            }
                            sx={{ cursor: "pointer", p: 0.5 }}
                          >
                            <Settings sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar sorteo">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => editRaffle(raffle._id)}
                            sx={{ cursor: "pointer", p: 0.5 }}
                          >
                            <Edit sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            raffle.status !== 0
                              ? `No se puede bloquear (${RaffleUtils.resolveStatus(raffle.status)})`
                              : "Bloquear sorteo"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={raffle.status !== 0}
                              onClick={() =>
                                dispatch(
                                  setRaffleForDisableAct({
                                    raffleId: raffle._id,
                                    raffleName: raffle.name,
                                  }),
                                )
                              }
                              sx={{ cursor: raffle.status === 0 ? "pointer" : "default", p: 0.5 }}
                            >
                              <Lock sx={{ fontSize: 18 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  )
}
